import { debounce } from 'lodash-es'
import { ChannelEvent } from '@youyan/common'
import { useEmptyModal } from './empty.tsx'
import {
  addTeamSpace,
  createShareTemplate,
  createTaskEdit,
  createTaskTemplate,
  createTemplatePersonalSpaceForPieces,
  editAsForShare,
  getPersonalSpacePieces,
  getSpacePiecesDetail,
  saveAsPersonalSpaceForPieces,
} from '~/api'
import type { Template } from '~/api/interface'
import ViewProgress from '@/features/piecesView/progress.vue'
import {
  PIECES_VIDEO_BIT_TEXT,
  PIECES_VIDEO_RATE_TEXT,
  PIECES_VIDEO_TYPE_TEXT,
} from '~/constants/index.ts'
import { YY_COMMON_GENERATE_LIST } from '~/constants/config.ts'

interface ActionOption {
  chengpianCreatePermission: ActionListKey
  newWorkEditPermission?: ActionListKey
  createTemplate: (data: { id: number }) => Promise<any>
  createOpusFromChengpian?: (data: { id: number }) => Promise<any>
  shareIdKey: string
  collectModelIdKey: string
}

const actionMap: Partial<Record<SOURCE, ActionOption>> = {
  [SOURCE.COLLECT]: {
    chengpianCreatePermission: ActionListKey.COLLECTION_TEMPLATE,
    newWorkEditPermission: ActionListKey.COLLECTION_SAVE,
    createTemplate: createTaskTemplate,
    createOpusFromChengpian: createTaskEdit,
    shareIdKey: 'model_id',
    collectModelIdKey: 'model_id',
  },
  [SOURCE.MYSPACE]: {
    chengpianCreatePermission: ActionListKey.PERSONAL_TEMPLATE,
    newWorkEditPermission: ActionListKey.PERSONAL_SAVE,
    createTemplate: createTemplatePersonalSpaceForPieces,
    createOpusFromChengpian: saveAsPersonalSpaceForPieces,
    shareIdKey: 'id',
    collectModelIdKey: 'id',
  },
  [SOURCE.TEAM_SPACE]: {
    chengpianCreatePermission: ActionListKey.TEAM_CREATE_PIECES_TEMPLATE,
    createTemplate: addTeamSpace,
    shareIdKey: 'model_id',
    collectModelIdKey: 'model_id',
  },
  [SOURCE.SHARE_WITH_ME]: {
    chengpianCreatePermission: ActionListKey.TEAM_CREATE_PIECES_TEMPLATE,
    newWorkEditPermission: ActionListKey.TEAM_SAVE,
    createTemplate: createShareTemplate,
    createOpusFromChengpian: editAsForShare, // 只有分享的才调用这个
    shareIdKey: 'model_id',
    collectModelIdKey: 'model_id',
  },
}

// TODO: @zhouxianfeng 这里的 resourceLocation 有点问题
export const showPieces = debounce(
  (params: {
    val: Template
    id: number
    from: { target: TARGET_FROM; source: SOURCE }
    trackParams?: any
    reload?: (ids: number[] | any) => void
    shareToOuterUser?: boolean
    allowAccelerate?: boolean // 是否允许排队加速
    allowCancelQueue?: boolean // 是否允许取消排队加速
    actions?: {
      retry?: () => void // 重新合成
      cancel?: () => void // 取消合成
      speed?: () => void // 排队加速
      cancelSpeed?: () => void // 取消排队加速
    }
  }) => {
    const {
      val,
      from,
      trackParams,
      id,
      reload: paramsReload,
      actions: opAction,
    } = params
    // 设置轮询
    const pollInterval = ref()
    const defaultData = ref(val)
    const isCancelRoll = ref(false)
    const loading = ref(false)

    const url = computed(
      () => defaultData.value?.video_url || defaultData.value?.url?.[0]
    )

    let suffix = ''
    if (url.value) {
      if (url.value.includes('?')) {
        suffix = url.value.split('?')[0].split('.').pop()?.toLowerCase()
      } else {
        suffix = url.value.split('.').pop()?.toLowerCase()
      }
    }

    const { $t } = useNuxtApp()
    const resourceLocation = from

    if (['mov', 'mkv'].includes(suffix || '')) {
      message.warning(
        $t(
          'liu-lan-qi-zan-bu-zhi-chi-ci-ge-shi-shi-pin-bo-fang-qing-xia-zai-cheng-pian-hou-cha-kan'
        )
      )
    }

    if (trackParams) {
      trackEvent(TRACK_EVENT.CLICK_PLAY_VIDEO, trackParams)
    }

    const action = actionMap[resourceLocation?.source as SOURCE]

    const pollData = async () => {
      if (pollInterval.value) {
        clearTimeout(pollInterval.value)
        pollInterval.value = null
      }
      if (!defaultData.value || isCancelRoll.value) {
        return
      }
      const res: any = await getPersonalSpacePieces({
        id,
      })
      const data = res.data?.find((o: any) => o.id === (Number(id) || val.id))

      if ([EXECUTION_STATUS.CANCELED].includes(data?.status) || !data?.id) {
        cancel()
        return
      }

      defaultData.value = {
        ...data,
        ...data?.xihe_task_progress_info,
      }

      if (
        [
          EXECUTION_STATUS.NOT_STARTED,
          EXECUTION_STATUS.DATA_INIT,
          EXECUTION_STATUS.WAITING,
          EXECUTION_STATUS.PROCESSING,
          EXECUTION_STATUS.ERROR,
        ].includes(defaultData.value.status)
      ) {
        pollInterval.value = setTimeout(() => {
          pollData()
        }, 3000)
      } else {
        clearTimeout(pollInterval.value)
      }
    }

    // 停止轮询
    const stopPolling = () => {
      if (pollInterval.value) {
        clearTimeout(pollInterval.value)
        pollInterval.value = null
      }
      isCancelRoll.value = true
      if (import.meta.client) {
        emitBroadcast(YY_COMMON_GENERATE_LIST, {
          event: ChannelEvent.SHOW_MESSAGE,
        })
      }
    }
    // 完成状态下获取成片信息
    const getPiecesInfo = async () => {
      try {
        loading.value = true
        const res = await getSpacePiecesDetail({
          id,
        })

        defaultData.value = {
          like: val?.like,
          ...val,
          ...res.data,
        }
        loading.value = false
      } catch (error) {
        loading.value = false
      }
    }

    // 在组件挂载时开始轮询
    watch(
      () => val,
      (cur) => {
        if (![EXECUTION_STATUS.FINISHED].includes(val?.status) && cur) {
          pollData()
        } else {
          getPiecesInfo()
        }
      },
      { immediate: true }
    )

    // 在组件卸载时停止轮询
    onUnmounted(() => {
      stopPolling()
    })

    const cancel = () => {
      stopPolling()

      modal.destroy()
    }
    const processInfo = computed(() => ({
      [$t('shi-pin-ming-cheng')]: val.title,
      [$t('fen-bian-shuai')]:
        PIECES_VIDEO_RATE_TEXT[
          defaultData.value.output_resolution || defaultData.value.resolution
        ],
      [$t('ge-shi')]: PIECES_VIDEO_TYPE_TEXT()[defaultData.value.file_format],
      [$t('zhen-shuai')]: `${defaultData.value.frame}fps`,
      // [$t('ma-shuai')]: PIECES_VIDEO_BIT_TEXT()[defaultData.value.max_bitrate],
      ...(defaultData.value?.watermark_info?.text && {
        [$t('shui-yin-wen-zi')]: defaultData.value?.watermark_info.text,
      }),
    }))

    const setLike = (like: any) => {
      defaultData.value.like = like
      val.like = like
    }

    const renderInfo = () => {
      return Object.entries(processInfo.value).map((item, index) => {
        return (
          <div key={index} class="mb-[12px] w-full">
            <div class="font-medium text-font-60 flex-y-center gap-[16px] text-12">
              <div class={isChinese() ? 'min-w-[50px]' : 'min-w-[65px]'}>
                {item[0]}
              </div>
              <div class="text-font-1 truncate flex-1" title={item[1]}>
                {item[1]}
              </div>
            </div>
          </div>
        )
      })
    }

    const modal = useEmptyModal({
      closable: false,
      maskClosable: true,
      onCancel: () => stopPolling(),
      width: '932px',
      source: resourceLocation?.source,
      content: () => {
        return (
          <div>
            <a-spin spinning={loading.value}>
              <div
                onClick={cancel}
                class="min-w-[48px] px-[12px] rounded-[8px] -top-[42px] cursor-pointer right-[8px]  flex-y-center absolute h-[32px] justify-center bg-[rgba(0,0,0,0.3)] text-white text-12 font-semibold border-solid border-[1px] border-[var(--pieces-view-close-border)]"
              >
                {url.value ? $t('shou-qi') : $t('shou-qi-ren-wu')}
              </div>
              <div>
                <div class="flex p-[0]">
                  <div class="w-[640px]">
                    <ViewProgress
                      class="w-full"
                      data={defaultData.value}
                      setLike={setLike}
                      paramsReload={paramsReload}
                      opAction={opAction}
                      action={action}
                      reload={pollData}
                      allowAccelerate={params?.allowAccelerate}
                      cancel={cancel}
                      shareToOuterUser={params?.shareToOuterUser}
                      from={from}
                      allowCancelQueue={params.allowCancelQueue}
                    />
                  </div>
                  <div class="flex-1 p-[24px_28px]">
                    <div class="flex justify-between">
                      <div class="text-14 font-bold text-font-1">
                        {$t('cheng-pian-xin-xi')}
                      </div>
                    </div>
                    <div class="mt-[10px] mb-[16px] w-[230px] h-[132px] bg-back-2 rounded-[8px]">
                      <img
                        src={val.cover || defaultData.value.cover_url}
                        class={
                          val.vertical ||
                          defaultData.value?.project_resolution === '1080x1920'
                            ? 'w-full rounded-[8px] h-full object-contain'
                            : 'w-full rounded-[8px] h-full object-cover '
                        }
                        alt={val.title}
                      />
                    </div>
                    {defaultData.value.id && (
                      <div class="w-[230px]">{renderInfo()}</div>
                    )}
                  </div>
                </div>
              </div>
            </a-spin>
          </div>
        )
      },
    })

    return modal
  },
  200
)
